/**
 * Case Conversion Utilities
 *
 * Automatically converts between snake_case (API) and camelCase (Frontend).
 * Used by API client interceptors for seamless conversion.
 */

// ===== Type-level case conversion =====
// Mirrors the runtime interceptor conversion so CID-generated types match camelCase usage.

type SnakeToCamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<SnakeToCamelCase<U>>}`
  : S

export type CamelCaseKeys<T> = T extends Array<infer U>
  ? Array<CamelCaseKeys<U>>
  : T extends Record<string, unknown>
    ? { [K in keyof T as K extends string ? SnakeToCamelCase<K> : K]: CamelCaseKeys<T[K]> }
    : T

/**
 * Convert string from snake_case to camelCase
 */
export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

/**
 * Convert string from camelCase to snake_case
 */
export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

/**
 * Check if value is a plain object (not Date, RegExp, etc.)
 */
function isPlainObject(obj: any): boolean {
  if (obj === null || typeof obj !== 'object') {
    return false
  }
  const proto = Object.getPrototypeOf(obj)
  return proto === Object.prototype || proto === null
}

/**
 * Deep convert object keys from snake_case to camelCase
 */
export function convertKeysToCamel<T = any>(obj: any): T {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeysToCamel(item)) as any
  }

  if (isPlainObject(obj)) {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = snakeToCamel(key)
      acc[camelKey] = convertKeysToCamel(obj[key])
      return acc
    }, {} as any)
  }

  return obj
}

/**
 * Deep convert object keys from camelCase to snake_case
 */
export function convertKeysToSnake<T = any>(obj: any): T {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeysToSnake(item)) as any
  }

  if (isPlainObject(obj)) {
    return Object.keys(obj).reduce((acc, key) => {
      const snakeKey = camelToSnake(key)
      acc[snakeKey] = convertKeysToSnake(obj[key])
      return acc
    }, {} as any)
  }

  return obj
}
